/**
 * Plain-words presentation copy for the RBAC Permissions screen (ADR-0046).
 *
 * Presentation-only — safe to hardcode (it's copy, not tenant data). Maps
 * each CAPABILITIES-tuple string to a warm, plain-words label + one-line
 * description (no snake_case, no engine words, no em dashes on screen —
 * brand voice §10), grouped by domain (mirrors the CAPABILITIES tuple's
 * own inline comment groups in src/lib/capabilities.ts). A capability
 * added to the tuple without a COPY entry falls back to a humanized
 * version of its key; a capability left out of PERMISSION_GROUPS is NOT
 * rendered at all (the screen walks the groups, not the tuple), which
 * is why tests/ui/permissionCopy.test.ts pins that every capability in
 * the tuple sits in exactly one group.
 */

import type { Capability } from './capabilities';
import type { UserTenantRole } from './types';
import { ROLE_DISPLAY } from './teamRoles';
import { CLIENT_RECORD_ACCESS_COPY } from './clientRecordAccess';

export interface CapabilityCopy {
  label: string;
  blurb: string;
}

export interface PermissionGroup {
  title: string;
  capabilities: readonly Capability[];
}

export const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  {
    title: 'Directory',
    capabilities: [
      'read',
      'direct_write',
      'suggest',
      'add_notes',
      'approve',
      'read_all_suggestions',
      'manage_lookups',
      'manage_team',
      'manage_staff_access',
      'recommend_provider',
      'delete_provider_note',
      'merge_providers',
      'archive_providers',
      'view_directory_gaps',
    ],
  },
  {
    title: 'Staff availability',
    capabilities: ['manage_staff_availability'],
  },
  {
    title: 'Time',
    capabilities: ['log_internal_time', 'export_payroll_hours'],
  },
  {
    title: 'AI tools',
    capabilities: ['use_ai_tools'],
  },
  {
    title: 'Olive hub',
    capabilities: ['view_staff_tools', 'view_office_tools', 'view_exec_tools', 'manage_announcements'],
  },
  {
    title: 'Pre-billing',
    capabilities: ['view_prebilling', 'view_master_prebilling'],
  },
  {
    title: 'Invoices',
    capabilities: ['compile_invoice', 'approve_invoice', 'issue_invoice', 'void_invoice', 'view_invoice'],
  },
  {
    title: 'Rates',
    capabilities: ['manage_client_rates'],
  },
  {
    title: 'Billing details',
    capabilities: ['set_bill_to'],
  },
  {
    title: 'QuickBooks',
    capabilities: [
      'connect_billing_integration',
      'manage_billing_integration',
      'push_invoice',
      'manage_qbo_customer_links',
    ],
  },
  {
    title: 'Workspace',
    capabilities: [
      'view_workspace',
      'manage_work_items',
      'manage_own_tasks',
      'moderate_work_item_notes',
      'manage_checklist_templates',
      'manage_workspace_processes',
    ],
  },
  {
    title: 'Staff profiles',
    capabilities: ['manage_staff_profiles', 'view_staff_private_contact'],
  },
  {
    title: 'Clinical record',
    capabilities: [
      'view_all_clinical',
      'view_engagement_clients',
      'read_clinical_record',
      'write_clinical_notes',
      'moderate_clinical_notes',
      'write_demo_clinical',
      'manage_client_identity',
      'edit_client_details',
      'assign_care_managers',
    ],
  },
  {
    title: 'Reports',
    capabilities: ['view_reports', 'ingest_operating_metrics', 'view_referral_reports'],
  },
  {
    title: 'Permissions',
    capabilities: ['manage_permissions'],
  },
  {
    title: 'Errors',
    capabilities: ['view_errors'],
  },
];

export const CAPABILITY_COPY: Partial<Record<Capability, CapabilityCopy>> = {
  read: { label: 'See the directory', blurb: 'Look up organizations and people in the directory.' },
  view_errors: {
    label: 'See what failed',
    blurb: 'Open Admin › Errors: every failure, with who hit it, where, and the reference to quote.',
  },
  direct_write: {
    label: 'Edit the directory directly',
    blurb:
      'Change an organization’s or person’s details, tags, service areas and links to people without waiting on a review, and mark a person’s record as checked. Archiving or restoring a listing is separate.',
  },
  suggest: {
    label: 'Suggest a change',
    blurb: 'Propose a directory edit for a steward to review.',
  },
  add_notes: { label: 'Add notes', blurb: 'Leave a note on a directory record, and delete the notes you wrote.' },
  approve: {
    label: 'Approve suggestions',
    blurb: 'Review and apply suggested directory changes, including a suggestion to remove a listing.',
  },
  read_all_suggestions: {
    label: 'See every suggestion',
    blurb:
      'See every pending suggested change, and the Change log of directory edits and notes. Team changes show only to seats that also see staff tools.',
  },
  manage_lookups: {
    label: 'Manage categories and tags',
    blurb: 'Add, rename, or retire the directory’s categories and tags.',
  },
  manage_team: { label: 'Manage the team', blurb: 'Add, remove, and change roles for people on the team.' },
  manage_staff_access: {
    label: 'Onboard and offboard staff',
    blurb: 'Add a person to the roster or remove them, and grant, change, or revoke their sign-in and role.',
  },
  recommend_provider: {
    label: 'Recommend a provider',
    blurb: 'Mark a provider as recommended in the directory.',
  },
  delete_provider_note: {
    label: 'Delete and moderate provider notes',
    blurb:
      'Delete anyone’s note on an organization or a person. A deleted note is gone for good; the trail keeps who deleted it and when, but not its words. Anyone who can add notes deletes their own without this. The owner, operations, the business manager and administrative support hold this by default.',
  },
  merge_providers: { label: 'Merge duplicates', blurb: 'Combine duplicate directory entries into one.' },
  archive_providers: {
    label: 'Archive and restore listings',
    blurb: 'Take an organization or person out of the directory or bring one back, and remove a document someone else attached.',
  },
  view_directory_gaps: {
    label: 'See map entries without a location',
    blurb: 'Show the Directory map’s lists of organizations and people it could not place, to fix their addresses.',
  },
  manage_staff_availability: {
    label: 'Manage staff availability',
    blurb: 'Update who is available and when.',
  },
  log_internal_time: {
    label: 'Log their own internal time',
    blurb: 'Record their own admin, training, and meeting hours on their home page.',
  },
  export_payroll_hours: {
    label: 'Download the payroll hours file',
    blurb: 'Download every staff member’s hours for a pay period, ready for payroll.',
  },
  use_ai_tools: { label: 'Use AI tools', blurb: 'Open the AI tools portal.' },
  view_staff_tools: {
    label: 'See staff tools',
    blurb:
      'The staff hub, the Team page with its SPICE week (client names and visit outcomes), their own work items, announcements, the bell, and team changes in the Change log.',
  },
  view_office_tools: { label: 'See office tools', blurb: 'View the office tier of Olive.' },
  view_exec_tools: { label: 'See executive tools', blurb: 'View the executive tier of Olive.' },
  manage_announcements: {
    label: 'Manage announcements',
    blurb: 'Post and edit the announcements banner.',
  },
  compile_invoice: { label: 'Compile invoices', blurb: 'Build a draft invoice from billable activity.' },
  approve_invoice: { label: 'Approve invoices', blurb: 'Move a draft invoice forward for issuing.' },
  issue_invoice: { label: 'Issue invoices', blurb: 'Send an invoice to the client.' },
  void_invoice: { label: 'Void invoices', blurb: 'Cancel an issued invoice.' },
  view_invoice: {
    label: 'See invoices',
    blurb: 'View invoices, their previews and history, and the full Pre-billing tab with rates.',
  },
  view_prebilling: {
    label: 'Check case notes before billing',
    blurb:
      'Open a client’s Pre-billing tab to check each case note’s hours, rate band and invoice line before it bills. Rates and amounts show only with See invoices.',
  },
  view_master_prebilling: {
    label: 'See every client’s pre-billing',
    blurb:
      'Open the Pre-billing page: every client’s note lines for a date range, with who still has notes in draft. With See invoices, also see each Ready line’s amount.',
  },
  manage_client_rates: {
    label: 'Set client rates',
    blurb: 'Set or change a client’s per-band billing rates.',
  },
  set_bill_to: {
    label: 'Choose who receives the bill',
    blurb:
      'Mark which contact receives the bill, and add or change a client’s Billing details on the Pre-billing tab. Adding and editing contacts needs only Write clinical notes. Refresh from care contact also needs Add, archive and edit client identity.',
  },
  connect_billing_integration: {
    label: 'Connect or reconnect QuickBooks',
    blurb: 'Connect the QuickBooks company, or reconnect it when the connection has expired.',
  },
  manage_billing_integration: {
    label: 'Disconnect QuickBooks',
    blurb: 'Disconnect the QuickBooks company. Switching to a different company starts here.',
  },
  push_invoice: {
    label: 'Send invoices to QuickBooks',
    blurb: 'Push an issued invoice into QuickBooks.',
  },
  manage_qbo_customer_links: {
    label: 'Match clients to QuickBooks customers',
    blurb: 'Link a client to its QuickBooks customer, or unlink one.',
  },
  view_workspace: {
    label: 'See the workspace',
    blurb: 'View the workspace boards and cards. It also shows who the office has open to new clients.',
  },
  manage_work_items: {
    label: 'Manage cards',
    blurb: 'Create, move, and edit workspace cards. It also shows who the office has open to new clients.',
  },
  manage_own_tasks: {
    label: 'Manage your own tasks',
    blurb: 'Keep a personal to-do list in the Workspace: add, edit, complete, and remove your own tasks, and leave a card shared with you. No boards, and never anyone else’s task.',
  },
  moderate_work_item_notes: {
    label: 'Edit or delete anyone’s card notes',
    blurb: 'Change or remove a note another person left on a workspace card. Everyone can edit their own.',
  },
  manage_checklist_templates: {
    label: 'Manage checklist templates',
    blurb: 'Author the checklist templates cards follow.',
  },
  manage_workspace_processes: {
    label: 'Manage workspace boards',
    blurb: 'Create and edit workspace processes and stages.',
  },
  manage_staff_profiles: {
    label: 'Manage staff profiles',
    blurb: 'Edit a care manager’s bio and photo.',
  },
  view_staff_private_contact: {
    label: 'See staff home addresses and personal phones',
    blurb: 'See the private contact details on the Team roster: home address and personal phone. The office seats hold this by default.',
  },
  view_all_clinical: {
    label: 'See every client’s record',
    blurb: 'Open any client’s clinical record, whatever their service or care team.',
  },
  view_engagement_clients: {
    label: 'See every client in their service',
    blurb: `Open every care-management client when they do care management, and every SPICE client when they do SPICE, as set on their roster row. When ${CLIENT_RECORD_ACCESS_COPY.heading} (above) is ${CLIENT_RECORD_ACCESS_COPY.options.all_staff.label}, internal staff who hold this open every client, except a Directory Editor. Line care managers and SPICE therapists hold this by default.`,
  },
  read_clinical_record: { label: 'Open clinical records', blurb: 'View a client’s clinical record.' },
  write_clinical_notes: {
    label: 'Write clinical notes',
    blurb: 'Author notes and confirm entries in the clinical record.',
  },
  moderate_clinical_notes: {
    label: 'Edit or remove anyone’s clinical notes',
    blurb: 'Change or remove a note someone else wrote on a client’s record. Every change is recorded. Everyone can change their own.',
  },
  write_demo_clinical: {
    label: 'Write in the demo record',
    blurb: 'Author notes in the board demo client only.',
  },
  manage_client_identity: {
    label: 'Add, archive and edit client identity',
    blurb:
      'Add, archive and restore clients, and change a client’s name, date of birth, gender, status, date of death, service start date and addresses. With Choose who receives the bill, also use Refresh from care contact on Billing details.',
  },
  edit_client_details: {
    label: 'Edit everyday client details',
    blurb:
      'Change a client’s phone, email, preferred name, pronouns, photo, relationship status, referred by and in-home support, their service, SPICE therapist, additional SPICE staff and SPICE schedule, on clients they can open.',
  },
  assign_care_managers: {
    label: 'Assign care managers',
    blurb: 'Change a client’s care manager or add another, on any client they can open.',
  },
  view_reports: { label: 'See reports', blurb: 'View the operating reports pages.' },
  ingest_operating_metrics: {
    label: 'Upload weekly metrics',
    blurb: 'Upload the weekly operating-metrics numbers.',
  },
  view_referral_reports: {
    label: 'See referral reports',
    blurb: 'View referral-partner analytics.',
  },
  manage_permissions: {
    label: 'Manage permissions',
    blurb: 'Open this screen and change what each role can do.',
  },
};

/** Humanized fallback for a capability with no copy entry (never drop a row). */
export function humanizeCapability(cap: string): string {
  return cap
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function capabilityLabel(cap: Capability): string {
  return CAPABILITY_COPY[cap]?.label ?? humanizeCapability(cap);
}

export function capabilityBlurb(cap: Capability): string {
  return CAPABILITY_COPY[cap]?.blurb ?? '';
}

/**
 * Plain-words role name for the column headers.
 *
 * COLLAPSED 2026-08-27 onto ROLE_DISPLAY (src/lib/teamRoles.ts). This was
 * the THIRD role-label map in the tree and it disagreed with the other
 * two: 'Care manager' here where ROLE_DISPLAY said 'Primary CM' and
 * people.ts said 'Clinical Team'. Three names, one seat, depending on
 * which screen you were looking at.
 *
 * The fallback stays local: an unrecognized role in the Permissions
 * matrix should still render something readable rather than a raw key.
 */
export function roleLabel(role: string): string {
  return role in ROLE_DISPLAY ? ROLE_DISPLAY[role as UserTenantRole] : humanizeCapability(role);
}

/** Blurb for a role — the one-line answer to "what is this seat for?". */
export { roleBlurb } from './teamRoles';
